import React from 'react';
import { Box, Text } from '@atlaskit/primitives';
import { cssMap, jsx } from '@atlaskit/css';
import { components, type GroupHeadingProps, type GroupProps } from '@atlaskit/react-select';
import { token } from '@atlaskit/tokens';
import { ALL_COLUMNS_ID, SELECTED_COLUMNS_ID } from '../../constants.tsx';
import type { SelectOption } from '../../../../common/types.tsx';

export type Props = GroupHeadingProps<SelectOption> & {
	data: { label?: string | undefined; id?: string };
	totalCount: number | null;
};

const styles = cssMap({
	groupHeading: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	borderGroup: {
		paddingTop: token('space.0'),
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
		marginBottom: token('space.100'),
	},
	allColumnsGroup: {
		paddingTop: token('space.0'),
	},
});

const HeadingContent = ({ label, totalCount }: { label?: string; totalCount?: number | null }) => {
	return (
		<Box xcss={styles.groupHeading} paddingBlockEnd="space.075" paddingInline="space.200">
			<Text size="small" weight="bold" color="color.text.subtle">
				{label?.toLocaleUpperCase()}
			</Text>
			{totalCount && (
				<Text size="small" color="color.text.subtle">
					{totalCount}
				</Text>
			)}
		</Box>
	);
};

export const GroupHeading = ({ data, totalCount, ...restProps }: Props): React.JSX.Element => {
	const { id, label } = data;
	switch (id) {
		case ALL_COLUMNS_ID:
			return <HeadingContent label={label} totalCount={totalCount} />;

		default:
			return <components.GroupHeading {...restProps} data={data} />;
	}
};

export const GroupHeadingNew = ({
	data,
	...props
}: Omit<Props, 'totalCount'>): React.JSX.Element | null => {
	const { id } = data;

	switch (id) {
		// no heading for these groups
		case ALL_COLUMNS_ID:
		case SELECTED_COLUMNS_ID:
			return null;

		default:
			return <components.GroupHeading {...props} data={data} />;
	}
};

type Group = GroupProps<SelectOption> & {
	data: { label?: string | undefined; id?: string };
};

export const Group = ({ data, ...props }: Group): React.JSX.Element => {
	const { id } = data;

	// selected columns group should have a border bottom divider
	if (id === SELECTED_COLUMNS_ID) {
		return <components.Group {...props} data={data} xcss={styles.borderGroup} />;
	}

	if (id === ALL_COLUMNS_ID) {
		return <components.Group {...props} data={data} xcss={styles.allColumnsGroup} />;
	}

	return <components.Group {...props} data={data} />;
};
